import React, { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SignUpForm from '../components/SignUpForm';
import { portalBase } from '../lib/api';
import { SignInError, portalLandingFor, requestCode, verifyCode, type Channel } from '../lib/signIn';

/**
 * The one way into the portal. `/sign-in` signs donors and partners in: email or phone → a 6-digit
 * code → the portal, already signed in. The page holds no session of its own: it hands the one it
 * receives to the portal (`portalLandingFor`) and leaves. `/signup` is the same page on its other
 * tab, where a new partner organization applies for an account (`SignUpForm`).
 */
type Step = 'identifier' | 'code';
export type Mode = 'sign-in' | 'sign-up';

const MODES: readonly { mode: Mode; path: string; label: string }[] = [
  { mode: 'sign-in', path: '/sign-in', label: 'Sign in' },
  { mode: 'sign-up', path: '/signup', label: 'Create an account' },
];

const INPUT_CLASS = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50';

const SignInForm: React.FC = () => {
  const [channel, setChannel] = useState<Channel>('email');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('identifier');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const isEmail = channel === 'email';

  const switchChannel = (next: Channel): void => {
    if (next === channel) return;
    setChannel(next);
    setIdentifier('');
    setCode('');
    setStep('identifier');
    setError(null);
    setNotice(null);
  };

  const sendCode = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      await requestCode(channel, identifier.trim());
      setStep('code');
      // A 200 is acceptance, not delivery: the server will not say whether the address is known.
      setNotice(`If ${isEmail ? 'that email address' : 'that number'} is known to us, a 6-digit code is on its way.`);
    } catch (err) {
      setError(err instanceof SignInError ? err.message : 'Could not send the code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const session = await verifyCode(channel, identifier.trim(), code.trim());
      window.location.replace(portalLandingFor(session));
    } catch (err) {
      setError(err instanceof SignInError ? err.message : 'Invalid or expired code. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
        <p className="mt-4 text-center text-sm text-gray-500">
          Donors see their giving history, tax certificates and recurring gifts. Partners open the portal.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-2 rounded-full bg-gray-100 p-1">
          {(['email', 'sms'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => switchChannel(c)}
              className={`rounded-full py-2 text-sm font-medium transition-colors ${channel === c ? 'bg-card text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {c === 'email' ? 'Email' : 'Phone'}
            </button>
          ))}
        </div>

        {step === 'identifier' ? (
          <form onSubmit={sendCode} className="mt-6 space-y-4">
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1">{isEmail ? 'Email address' : 'Phone number'}</label>
              <input
                id="identifier"
                className={INPUT_CLASS}
                type={isEmail ? 'email' : 'tel'}
                inputMode={isEmail ? 'email' : 'tel'}
                autoComplete={isEmail ? 'email' : 'tel'}
                placeholder={isEmail ? 'you@example.com' : '+14155550123'}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={loading}
                required
              />
              {!isEmail && <p className="mt-1 text-xs text-gray-400">Use international format, e.g. +14155550123. Phone sign-in is for donors.</p>}
            </div>
            <button type="submit" className="btn-primary w-full px-6 py-3 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading || !identifier.trim()}>
              {loading ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">6-digit code</label>
              <input
                id="code"
                className={`${INPUT_CLASS} text-center text-2xl tracking-[0.5em]`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                disabled={loading}
                required
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-400">Sent to {identifier.trim()}</p>
            </div>
            <button type="submit" className="btn-primary w-full px-6 py-3 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading || code.trim().length < 6}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('identifier'); setCode(''); setError(null); setNotice(null); }}
              className="w-full px-6 py-3 rounded-full font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={loading}
            >
              Use a different {isEmail ? 'email' : 'phone number'}
            </button>
          </form>
        )}

        {notice && <p className="mt-4 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700" role="status">{notice}</p>}
        {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p>}

        <p className="mt-6 text-center text-xs text-gray-400">
          By signing in you agree to the{' '}
          <a href={`${portalBase}/terms`} target="_blank" rel="noopener" className="text-primary hover:underline">Terms of Service</a> and the{' '}
          <a href={`${portalBase}/privacy`} target="_blank" rel="noopener" className="text-primary hover:underline">Privacy Policy</a>.
        </p>
    </>
  );
};

const SignInPage: React.FC<{ mode: Mode }> = ({ mode }) => (
  <section className="py-20 px-6">
    <div className={`mx-auto card p-8 ${mode === 'sign-up' ? 'max-w-2xl' : 'max-w-md'}`}>
      <h1 className="text-2xl font-bold text-center">{mode === 'sign-up' ? 'Join Pawsitive Future' : 'Sign in'}</h1>
      <nav className="mt-6 grid grid-cols-2 gap-2 rounded-full bg-gray-100 p-1" aria-label="Sign in or create an account">
        {MODES.map((m) => (
          <Link
            key={m.mode}
            to={m.path}
            replace
            aria-current={m.mode === mode ? 'page' : undefined}
            className={`rounded-full py-2 text-center text-sm font-medium transition-colors ${m.mode === mode ? 'bg-card text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {m.label}
          </Link>
        ))}
      </nav>
      {mode === 'sign-up' ? <SignUpForm /> : <SignInForm />}
    </div>
  </section>
);

export default SignInPage;
